import {
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TdHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

import {
  dataTableCellDataClass,
  dataTableCellOffsetClass,
  dataTableHeaderRowTextClass,
  dataTableRowClass,
  dataTableTheadClass,
} from './classes';

export function DataTableTable({
  children,
  'aria-label': ariaLabel,
  className,
}: {
  readonly children: ReactNode;
  readonly 'aria-label'?: string;
  readonly className?: string;
}): ReactElement {
  return (
    <div className={cn('w-full min-w-0 overflow-x-auto', className)}>
      <table
        className="w-full caption-bottom border-collapse text-left text-sm"
        aria-label={ariaLabel}
      >
        {children}
      </table>
    </div>
  );
}

export function DataTableThead({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactElement {
  return <thead className={cn(dataTableTheadClass, className)}>{children}</thead>;
}

export function DataTableTbody({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactElement {
  return <tbody className={className}>{children}</tbody>;
}

export function DataTableHeaderRow({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactElement {
  return <tr className={cn(dataTableHeaderRowTextClass, className)}>{children}</tr>;
}

export function DataTableTr({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>): ReactElement {
  return (
    <tr className={cn(dataTableRowClass, className)} {...rest}>
      {children}
    </tr>
  );
}

export function DataTableTd({
  className,
  dataOffset,
  align = 'left',
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & {
  readonly dataOffset?: boolean;
  readonly align?: 'left' | 'right' | 'center';
}): ReactElement {
  return (
    <td
      className={cn(
        dataTableCellDataClass,
        dataOffset && dataTableCellOffsetClass,
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      {...rest}
    />
  );
}

export function DataTablePill({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded-full',
        'border border-transparent',
        'bg-secondary px-2.5 py-0.5 text-left text-[11px] font-medium text-secondary-foreground',
        'sm:text-xs',
        className,
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
